import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, Users, Star, TrendingUp, Plus, Eye, Edit3, BarChart2, Settings } from '../../components/CustomIcons';
import EmptyState from '../../components/EmptyState';
import WelcomeModal, { useMarkOnboardingPath } from '../../components/WelcomeModal';

function StatCard({ icon: Icon, value, label, color = 'var(--orange)' }) {
  return (
    <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'var(--orange-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={18} style={{ color }} />
        </div>
        <span style={{ fontSize: '10px', color: 'var(--success)', fontWeight: 600, background: 'var(--success-bg)', padding: '2px 8px', borderRadius: '4px' }}>LIVE</span>
      </div>
      <div>
        <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)' }}>{value}</div>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{label}</div>
      </div>
    </div>
  );
}

export default function ProviderPortal() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useMarkOnboardingPath('/marketplace/provider');

  useEffect(() => {
    authFetch('/marketplace/provider/dashboard')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '32px', background: 'var(--bg-page)', minHeight: '100vh' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div className="skeleton" style={{ height: '32px', width: '240px', borderRadius: '8px', marginBottom: '24px' }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
            {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: '110px', borderRadius: '14px' }} />)}
          </div>
        </div>
      </div>
    );
  }

  const { provider, stats, recent_courses } = data || { provider: {}, stats: {}, recent_courses: [] };

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="provider-portal">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div className="overline" style={{ marginBottom: '4px' }}>Provider Portal</div>
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: '0 0 4px 0' }}>
              {provider.name || 'Your Organization'}
            </h1>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)', background: 'var(--success-bg)', border: '1px solid var(--success)', padding: '2px 10px', borderRadius: '5px', color: 'var(--success)' }}>
              Approved Partner
            </span>
          </div>
          <Link to="/marketplace/provider/courses/new" className="btn-primary" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
            data-testid="new-course-btn">
            <Plus size={16} />
            New Course
          </Link>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          <StatCard icon={BookOpen} value={stats.total_courses || 0} label="Total Courses" />
          <StatCard icon={Eye} value={stats.published_courses || 0} label="Published" color="var(--success)" />
          <StatCard icon={Edit3} value={stats.draft_courses || 0} label="Drafts" color="var(--warning)" />
          <StatCard icon={Users} value={(stats.total_enrollments || 0).toLocaleString()} label="Total Enrollments" />
          <StatCard icon={Star} value={stats.avg_rating > 0 ? stats.avg_rating.toFixed(1) : '—'} label="Avg Rating" />
        </div>

        {/* Recent Courses */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: 0 }}>
              Your Courses
            </h2>
            <Link to="/marketplace/provider/courses" style={{ fontSize: '13px', color: 'var(--orange)', fontWeight: 600, textDecoration: 'none', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Manage All →
            </Link>
          </div>

          {recent_courses.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No courses yet"
              subtitle="Publish your first course to start reaching incarcerated learners."
              action={{ label: 'Create Course', onClick: () => navigate('/marketplace/provider/courses/new') }}
              testId="provider-courses-empty"
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {recent_courses.map((course, i) => (
                <div key={course.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 0', borderBottom: i < recent_courses.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--orange-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <BookOpen size={18} style={{ color: 'var(--orange)' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 2px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {course.title}
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{course.category} • {course.duration_hours}h</p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)', margin: '0 0 2px 0' }}>{course.enrolled_count || 0}</p>
                    <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>enrolled</p>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '5px', background: course.status === 'published' ? 'var(--success-bg)' : 'var(--warning-bg)', color: course.status === 'published' ? 'var(--success)' : 'var(--warning)', flexShrink: 0 }}>
                    {course.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px', marginTop: '16px' }}>
          {[
            { to: '/marketplace/provider/courses', icon: BookOpen, label: 'Manage Courses', sub: 'View, edit, publish courses' },
            { to: '/marketplace/provider/analytics', icon: BarChart2, label: 'Analytics', sub: 'Enrollment & performance' },
            { to: '/marketplace/provider/settings', icon: Settings, label: 'Settings', sub: 'Organization profile' },
          ].map(link => (
            <Link key={link.to} to={link.to} style={{ textDecoration: 'none' }}>
              <div className="card" style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = ''}>
                <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'var(--orange-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <link.icon size={16} style={{ color: 'var(--orange)' }} />
                </div>
                <div>
                  <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', margin: 0 }}>{link.label}</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{link.sub}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <WelcomeModal />
    </div>
  );
}
